import React, { useState, useEffect } from 'react';
import axios from 'axios';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

// On-screen alphabet keyboard
function AlphabetKeyboard({ onSelect, selected, label }) {
  const letters = 'ABCDEFGHIJKLMNOPQRSTUVWXYZ'.split('');
  return (
    <div className="w-full">
      {label && <p className="text-center text-gray-500 text-sm mb-3 font-medium">{label}</p>}
      <div className="grid grid-cols-9 gap-1.5">
        {letters.map(l => (
          <button
            key={l}
            data-testid={`alpha-${l}`}
            onClick={() => onSelect(l)}
            className={`h-10 rounded-lg text-base font-bold border transition-all active:scale-95 ${
              selected === l
                ? 'bg-black text-white border-black'
                : 'bg-white text-gray-800 border-gray-300 hover:border-gray-500 hover:bg-gray-50'
            }`}
          >
            {l}
          </button>
        ))}
      </div>
    </div>
  );
}

// On-screen numeric keyboard for DOB
function DobKeyboard({ value, onChange }) {
  const display = formatDobDisplay(value);

  function handleKey(k) {
    if (k === 'del') {
      onChange(value.slice(0, -1));
    } else if (value.length < 8) {
      onChange(value + k);
    }
  }

  return (
    <div className="w-full">
      {/* DOB display */}
      <div
        data-testid="dob-display"
        className="w-full mb-4 py-4 px-6 bg-gray-100 rounded-xl text-3xl font-mono font-bold text-center text-gray-900 tracking-widest border-2 border-gray-200"
      >
        {display || <span className="text-gray-400">DD / MM / YYYY</span>}
      </div>
      {/* Numpad */}
      <div className="grid grid-cols-3 gap-2">
        {['1','2','3','4','5','6','7','8','9','','0','del'].map((k, i) => (
          k === '' ? (
            <div key={i} />
          ) : (
            <button
              key={i}
              data-testid={`dob-key-${k}`}
              onClick={() => handleKey(k)}
              className={`h-14 rounded-xl text-xl font-bold border transition-all active:scale-95 ${
                k === 'del'
                  ? 'bg-red-50 text-red-600 border-red-200 hover:bg-red-100'
                  : 'bg-white text-gray-900 border-gray-300 hover:bg-gray-50 hover:border-gray-400'
              }`}
            >
              {k === 'del' ? '⌫' : k}
            </button>
          )
        ))}
      </div>
    </div>
  );
}

// Phone number keyboard
function PhoneKeyboard({ value, onChange }) {
  function handleKey(k) {
    if (k === 'del') {
      onChange(value.slice(0, -1));
    } else if (value.length < 11) {
      onChange(value + k);
    }
  }

  return (
    <div className="w-full">
      <div
        data-testid="phone-display"
        className="w-full mb-4 py-4 px-6 bg-gray-100 rounded-xl text-2xl font-mono font-bold text-center text-gray-900 tracking-wider border-2 border-gray-200"
      >
        {value ? formatPhone(value) : <span className="text-gray-400">07xxx xxxxxx</span>}
      </div>
      <div className="grid grid-cols-3 gap-2">
        {['1','2','3','4','5','6','7','8','9','','0','del'].map((k, i) => (
          k === '' ? (
            <div key={i} />
          ) : (
            <button
              key={i}
              data-testid={`phone-key-${k}`}
              onClick={() => handleKey(k)}
              className={`h-14 rounded-xl text-xl font-bold border transition-all active:scale-95 ${
                k === 'del'
                  ? 'bg-red-50 text-red-600 border-red-200 hover:bg-red-100'
                  : 'bg-white text-gray-900 border-gray-300 hover:bg-gray-50 hover:border-gray-400'
              }`}
            >
              {k === 'del' ? '⌫' : k}
            </button>
          )
        ))}
      </div>
    </div>
  );
}

function formatDobDisplay(raw) {
  if (!raw) return '';
  const d = raw.slice(0, 2);
  const m = raw.slice(2, 4);
  const y = raw.slice(4, 8);
  let out = d;
  if (raw.length >= 2) out += ' / ' + m;
  if (raw.length >= 4) out += ' / ' + y;
  return out.trim();
}

function formatPhone(raw) {
  if (!raw) return '';
  const p = raw.slice(0, 11);
  if (p.length <= 5) return p;
  if (p.length <= 8) return `${p.slice(0,5)} ${p.slice(5)}`;
  return `${p.slice(0,5)} ${p.slice(5,8)} ${p.slice(8)}`;
}

const STEPS = ['club', 'first_initial', 'surname_initial', 'dob', 'confirm_gymnast', 'phone', 'confirm_phone', 'done'];

export default function SpectatorRegisterPage() {
  const [step, setStep] = useState('club');
  const [clubs, setClubs] = useState([]);
  const [selectedClub, setSelectedClub] = useState(null);
  const [firstInitial, setFirstInitial] = useState('');
  const [surnameInitial, setSurnameInitial] = useState('');
  const [dob, setDob] = useState('');
  const [gymnast, setGymnast] = useState(null);
  const [phone, setPhone] = useState('');
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [competitionId, setCompetitionId] = useState('');

  useEffect(() => {
    axios.get(`${API}/register/clubs`)
      .then(r => {
        setClubs(r.data.clubs || []);
        setCompetitionId(r.data.competitionId || '');
      })
      .catch(() => setError('No competition is currently open. Please try again later.'));
  }, []);

  const lookupGymnast = async () => {
    if (dob.length !== 8) { setError('Please enter a full date of birth'); return; }
    setLoading(true);
    setError('');
    const dobFormatted = `${dob.slice(0,2)}/${dob.slice(2,4)}/${dob.slice(4,8)}`;
    try {
      const r = await axios.post(`${API}/register/lookup`, {
        clubId: selectedClub.id,
        firstInitial,
        surnameInitial,
        dob: dobFormatted,
      });
      if (r.data.alreadyRegistered) {
        setError('A phone number is already registered for this gymnast. Only one registration is allowed per gymnast.');
        return;
      }
      setGymnast(r.data);
      setCompetitionId(r.data.competitionId);
      setStep('confirm_gymnast');
    } catch (e) {
      setError(e.response?.data?.detail || 'No gymnast found matching those details. Please check and try again.');
    } finally {
      setLoading(false);
    }
  };

  const registerPhone = async () => {
    if (phone.length < 10) { setError('Please enter a valid UK phone number'); return; }
    setLoading(true);
    setError('');
    try {
      await axios.post(`${API}/register/spectator`, {
        gymnastId: gymnast.gymnastId,
        competitionId,
        phoneNumber: phone,
      });
      setStep('done');
    } catch (e) {
      setError(e.response?.data?.detail || 'Registration failed. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  const reset = () => {
    setStep('club');
    setSelectedClub(null);
    setFirstInitial('');
    setSurnameInitial('');
    setDob('');
    setGymnast(null);
    setPhone('');
    setError('');
  };

  return (
    <div className="min-h-screen bg-gray-50 flex flex-col items-center py-6 px-4">
      {/* Header */}
      <div className="w-full max-w-lg mb-6">
        <div className="flex items-center justify-center gap-3 mb-1">
          <img src="/scorz-logo.png" alt="Scorz" className="h-10" onError={e => e.target.style.display = 'none'} />
        </div>
        <h1 className="text-center text-2xl font-black text-gray-900 mt-2">SMS Score Alerts</h1>
        <p className="text-center text-sm text-gray-500 mt-1">Register to receive instant scores for your gymnast</p>
      </div>

      <div className="w-full max-w-lg bg-white rounded-2xl shadow-sm border border-gray-100 p-6">

        {/* Progress dots */}
        {step !== 'done' && (
          <div className="flex items-center justify-center gap-2 mb-6">
            {['club','first_initial','surname_initial','dob','confirm_gymnast','phone'].map((s, i) => (
              <div key={s} className={`rounded-full transition-all ${
                STEPS.indexOf(step) >= STEPS.indexOf(s) ? 'bg-black w-3 h-3' : 'bg-gray-200 w-2 h-2'
              }`} />
            ))}
          </div>
        )}

        {error && (
          <div data-testid="register-error" className="mb-4 p-3 bg-red-50 border border-red-200 rounded-xl text-red-700 text-sm font-medium text-center">
            {error}
            <button onClick={() => setError('')} className="block w-full mt-2 text-xs text-red-500 underline">Dismiss</button>
          </div>
        )}

        {/* STEP: Select club */}
        {step === 'club' && (
          <div>
            <h2 className="text-lg font-bold text-gray-900 mb-1">Select your gymnast's club</h2>
            <p className="text-sm text-gray-400 mb-4">Choose the club they are competing with today</p>
            {clubs.length === 0 && !error && (
              <p className="text-center text-gray-400 py-6">Loading clubs...</p>
            )}
            <div className="space-y-2 max-h-80 overflow-y-auto">
              {clubs.map(c => (
                <button
                  key={c.id}
                  data-testid={`club-${c.id}`}
                  onClick={() => { setSelectedClub(c); setStep('first_initial'); setError(''); }}
                  className="w-full text-left px-4 py-3 rounded-xl border border-gray-200 hover:border-gray-400 hover:bg-gray-50 font-semibold text-gray-900 transition-all"
                >
                  {c.name}
                </button>
              ))}
            </div>
          </div>
        )}

        {/* STEP: First initial */}
        {step === 'first_initial' && (
          <div>
            <button onClick={() => setStep('club')} className="text-xs text-gray-400 hover:text-gray-700 mb-3 flex items-center gap-1">← Back</button>
            <h2 className="text-lg font-bold text-gray-900 mb-1">First letter of first name</h2>
            <p className="text-sm text-gray-400 mb-4">
              e.g. for <strong>Emily</strong>, tap <strong>E</strong>
            </p>
            <AlphabetKeyboard
              selected={firstInitial}
              onSelect={l => { setFirstInitial(l); setError(''); setTimeout(() => setStep('surname_initial'), 200); }}
            />
          </div>
        )}

        {/* STEP: Surname initial */}
        {step === 'surname_initial' && (
          <div>
            <button onClick={() => setStep('first_initial')} className="text-xs text-gray-400 hover:text-gray-700 mb-3 flex items-center gap-1">← Back</button>
            <h2 className="text-lg font-bold text-gray-900 mb-1">First letter of surname</h2>
            <p className="text-sm text-gray-400 mb-4">
              e.g. for <strong>Smith</strong>, tap <strong>S</strong>
            </p>
            <AlphabetKeyboard
              selected={surnameInitial}
              onSelect={l => { setSurnameInitial(l); setError(''); setTimeout(() => setStep('dob'), 200); }}
            />
          </div>
        )}

        {/* STEP: DOB */}
        {step === 'dob' && (
          <div>
            <button onClick={() => setStep('surname_initial')} className="text-xs text-gray-400 hover:text-gray-700 mb-3 flex items-center gap-1">← Back</button>
            <h2 className="text-lg font-bold text-gray-900 mb-1">Enter date of birth</h2>
            <p className="text-sm text-gray-400 mb-4">e.g. 25 / 07 / 2015</p>
            <DobKeyboard value={dob} onChange={setDob} />
            <button
              data-testid="dob-confirm-btn"
              onClick={lookupGymnast}
              disabled={dob.length !== 8 || loading}
              className="w-full mt-5 py-4 bg-black text-white rounded-xl font-bold text-lg disabled:opacity-40 disabled:cursor-not-allowed transition-all active:scale-95"
            >
              {loading ? 'Searching...' : 'Find my gymnast'}
            </button>
          </div>
        )}

        {/* STEP: Confirm gymnast */}
        {step === 'confirm_gymnast' && gymnast && (
          <div>
            <h2 className="text-lg font-bold text-gray-900 mb-1">Is this your gymnast?</h2>
            <p className="text-sm text-gray-400 mb-5">Please confirm before continuing</p>
            <div data-testid="gymnast-confirm-card" className="p-5 rounded-xl border-2 border-black bg-gray-50 mb-6">
              <p className="text-2xl font-black text-gray-900">{gymnast.name}</p>
              <p className="text-base text-gray-500 mt-1">{gymnast.clubName}</p>
            </div>
            <div className="flex gap-3">
              <button
                data-testid="confirm-gymnast-no"
                onClick={reset}
                className="flex-1 py-3 rounded-xl border border-gray-300 text-gray-700 font-semibold hover:bg-gray-50"
              >
                No, try again
              </button>
              <button
                data-testid="confirm-gymnast-yes"
                onClick={() => { setError(''); setStep('phone'); }}
                className="flex-1 py-3 rounded-xl bg-black text-white font-bold hover:bg-gray-800 active:scale-95"
              >
                Yes, that's them
              </button>
            </div>
          </div>
        )}

        {/* STEP: Enter phone */}
        {step === 'phone' && (
          <div>
            <button onClick={() => setStep('confirm_gymnast')} className="text-xs text-gray-400 hover:text-gray-700 mb-3 flex items-center gap-1">← Back</button>
            <h2 className="text-lg font-bold text-gray-900 mb-1">Enter your phone number</h2>
            <p className="text-sm text-gray-400 mb-4">UK numbers only (07xxx xxxxxx)</p>
            <PhoneKeyboard value={phone} onChange={setPhone} />
            <button
              data-testid="phone-confirm-btn"
              onClick={() => { setError(''); setStep('confirm_phone'); }}
              disabled={phone.length < 10}
              className="w-full mt-5 py-4 bg-black text-white rounded-xl font-bold text-lg disabled:opacity-40 disabled:cursor-not-allowed transition-all active:scale-95"
            >
              Continue
            </button>
          </div>
        )}

        {/* STEP: Confirm phone */}
        {step === 'confirm_phone' && (
          <div>
            <button onClick={() => setStep('phone')} className="text-xs text-gray-400 hover:text-gray-700 mb-3 flex items-center gap-1">← Back</button>
            <h2 className="text-lg font-bold text-gray-900 mb-1">Double-check your number</h2>
            <p className="text-sm text-gray-400 mb-5">Scores will be sent to this number</p>
            <div data-testid="phone-confirm-display" className="p-5 rounded-xl border-2 border-black bg-gray-50 mb-6 text-center">
              <p className="text-3xl font-black text-gray-900 tracking-wider">{formatPhone(phone)}</p>
            </div>
            <p className="text-sm text-gray-500 text-center mb-5">
              Registering for <strong>{gymnast?.name}</strong>
            </p>
            <div className="flex gap-3">
              <button
                data-testid="phone-change-btn"
                onClick={() => setStep('phone')}
                className="flex-1 py-3 rounded-xl border border-gray-300 text-gray-700 font-semibold hover:bg-gray-50"
              >
                Change number
              </button>
              <button
                data-testid="register-submit-btn"
                onClick={registerPhone}
                disabled={loading}
                className="flex-1 py-3 rounded-xl bg-black text-white font-bold hover:bg-gray-800 disabled:opacity-50 active:scale-95"
              >
                {loading ? 'Registering...' : 'Confirm'}
              </button>
            </div>
          </div>
        )}

        {/* STEP: Done */}
        {step === 'done' && (
          <div data-testid="register-success" className="text-center py-4">
            <div className="w-16 h-16 bg-green-100 rounded-full flex items-center justify-center mx-auto mb-4">
              <svg className="w-8 h-8 text-green-600" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2.5} d="M5 13l4 4L19 7" />
              </svg>
            </div>
            <h2 className="text-2xl font-black text-gray-900 mb-2">You're registered!</h2>
            <p className="text-gray-500 mb-2">
              You'll receive an SMS whenever <strong>{gymnast?.name}</strong> receives a score today.
            </p>
            <p className="text-sm text-gray-400 mb-6">
              Check your phone for a welcome message.
            </p>
            <button
              data-testid="register-another-btn"
              onClick={reset}
              className="w-full py-3 rounded-xl border border-gray-300 text-gray-700 font-semibold hover:bg-gray-50"
            >
              Register another gymnast
            </button>
          </div>
        )}
      </div>

      <p className="mt-6 text-xs text-gray-400 text-center">
        Powered by Scorz · scorz.co.uk
      </p>
    </div>
  );
}
